import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import { useRouter } from 'vue-router';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js'; // Import CryptoJS
import useNotifications from '@/store/notification';
import { useEquipmentsStore } from '@/store/equipments';
import { useCartStore } from './cart';
import { resolveImageUrl } from '@/composables/useImageUrl';
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('auth');

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
const apiEncryptKey = import.meta.env.VITE_ENCRYPTION_KEY;

const encryptData = (data) => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), apiEncryptKey).toString();
};

const decryptData = (data) => {
  try {
    const bytes = CryptoJS.AES.decrypt(data, apiEncryptKey);
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
    return decryptedData ? JSON.parse(decryptedData) : null;
  } catch (error) {
    logger.error('Error decrypting cookie data', error);
    return null; // Return null if decryption fails
  }
};

function getCSRFToken() {
  const csrfCookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('csrftoken='));
  return csrfCookie ? csrfCookie.split('=')[1] : null;
}

axios.defaults.headers.common['X-CSRFToken'] = getCSRFToken();

// ---------- ADDRESS NORMALISER (UPDATED) ----------

const emptyAddress = {
  id: null,
  street_address: '',
  street_address2: '',
  city: '',
  state: '',
  zip_code: '',
  country: '',
  address_type: null,
  is_default: false,
  latitude: null,
  longitude: null,
  google_place_id: '',
  formatted_address: '',
  raw_geocode: null,
  full_address: '',
  has_coordinates: false,
};

/**
 * Normalise API user shape so frontend can always use `user.user_address`.
 * - If backend sends `address` (related Address instance), use that.
 * - If backend sends `user_address` (old shape / from cookie), use that.
 * - Also supports old naming like `street` if it ever existed.
 */
const normaliseUserAddress = (apiUser) => {
  if (!apiUser) return { ...emptyAddress };

  // Prefer new `address` relation, fallback to older `user_address`
  const address = apiUser.user_address || null;

    logger.debug('Normalising user address', { address_id: address?.id });

  if (!address) {
    return { ...emptyAddress };
  }

  // Allow some tolerance for older field names if they existed before
  const street_address =
    address.street_address ?? address.street ?? '';
  const street_address2 =
    address.street_address2 ?? address.line2 ?? '';
  const city = address.city ?? '';
  const state = address.state ?? '';
  const zip_code = address.zip_code ?? address.zip ?? '';
  const country = address.country ?? '';

  const latitude =
    address.latitude !== undefined ? address.latitude : null;
  const longitude =
    address.longitude !== undefined ? address.longitude : null;

  const full_address =
    address.full_address ??
    [street_address, street_address2, city, state, zip_code, country]
      .filter(Boolean)
      .join(', ');

  const has_coordinates =
    address.has_coordinates ??
    (latitude !== null && longitude !== null);

  return {
    id: address.id ?? null,
    street_address,
    street_address2,
    city,
    state,
    zip_code,
    country,
    address_type:
      address.address_type !== undefined ? address.address_type : null,
    is_default:
      address.is_default !== undefined ? address.is_default : false,
    latitude,
    longitude,
    google_place_id: address.google_place_id ?? '',
    formatted_address: address.formatted_address ?? full_address,
    raw_geocode:
      address.raw_geocode !== undefined ? address.raw_geocode : null,
    full_address,
    has_coordinates,
  };
};

export const useAuthStore = defineStore('auth', () => {
  const cartStore = useCartStore();
  const store = useEquipmentsStore();

  const redirectTo = ref('');

  const user = ref(null);
  // Initialise isOn separately; we’ll update it once user is loaded
  const isOn = ref(false);

  const showSidebar = ref(true); // Sidebar visibility state

  const loginError = ref('');
  const isLoading = ref(false);
  const router = useRouter();
  const { showNotification } = useNotifications();
  const activeSection = ref('personal-info');

  // Computed property for active section based on user role
  const roleSection = computed(() => {
    return user?.value?.role !== 'lessor' ? 'my-orders' : 'my-equipments';
  });

  const isAuthenticated = computed(() => !!user.value);

  // Normalise user image to always be an absolute URL
  const normaliseUser = (apiUser) => ({
    ...apiUser,
    image: resolveImageUrl(apiUser?.image_url || apiUser?.image) || null,
    user_address: normaliseUserAddress(apiUser),
  });

  // Restore user from cookie immediately (onMounted doesn't run in Pinia stores)
  const storedUser = Cookies.get('user');
  if (storedUser) {
    const decrypted = decryptData(storedUser);
    if (decrypted) {
      user.value = {
        ...decrypted,
        user_address: normaliseUserAddress(decrypted),
      };
      isOn.value = user.value?.role === 'lessor';
    }
  }

  const getUserData = async () => {
    try {
      if (!user.value?.id) {
        return;
      }

      const response = await axios.get(
        `${apiBaseUrl}/api/accounts/users/${user.value.id}/`,
        { withCredentials: true }
      );

      if (response.status === 200) {
        const apiUser = response.data;

        user.value = normaliseUser(apiUser);

        // Encrypt the user data and store it in cookies
        Cookies.set('user', encryptData(user.value), {
          sameSite: 'Lax',
        });

        isOn.value = user.value?.role === 'lessor';
      } else {
        logger.error(
          'User data fetch returned non-200',
          { status: response.status },
        );
      }
    } catch (error) {
      logger.error('Error fetching user data', error);
    }
  };

  // Function to navigate to a section
  const navigateToRoleSection = (sectionName) => {
    logger.debug('Navigating to role section', { section: sectionName, roleSection: roleSection.value });

    activeSection.value = roleSection.value;

    // Scroll to the section
    const sectionElement = document.getElementById(sectionName);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth' });
    }

    // Hide sidebar on small devices
    if (window.innerWidth < 1024) {
      showSidebar.value = false;
    }
  };

  const updateUserRole = async () => {
    try {
      const currentRole = user?.value?.role;
      if (!currentRole || !user.value?.id) return;

      // Proceed with updating the role first
      const updatedRole = currentRole === 'lessee' ? 'lessor' : 'lessee';

      const response = await axios.put(
        `${apiBaseUrl}/api/accounts/users/${user.value.id}/`,
        { role: updatedRole },
        { withCredentials: true }
      );

      // Normalise returned user and store in state + cookies
      const apiUser = response.data;
      user.value = normaliseUser(apiUser);

      isOn.value = user.value.role === 'lessor';

      Cookies.set('user', encryptData(user.value), {
        sameSite: 'Lax',
      });

      // Optionally re-fetch latest data from backend
      await getUserData();

      if (user?.value.role === 'lessee') {
        router.push('/new-profile/orders');
      } else if (user?.value.role === 'lessor') {
        router.push('/new-profile/items');
      } else {
        router.push('/new-profile');
      }

      navigateToRoleSection(roleSection.value);

      // Fetch user equipment data after the role change
      await store.fetchUserEquipments();

      showNotification(
        'Role Update Successful',
        `You are now a ${updatedRole}.`,
        'success'
      );
    } catch (error) {
      logger.error('Error updating role', error);
      showNotification(
        'Error',
        'Unable to switch role. Please try again.',
        'error'
      );
    }
  };
// auth.js
const login = async (email, password) => {
  isLoading.value = true;

  try {
    const response = await axios.post(
      `${apiBaseUrl}/api/accounts/login/`,
      { email, password },
      { withCredentials: true }
    );

    if (response.status === 200) {
      const apiUser = response.data;

      user.value = normaliseUser(apiUser);

      Cookies.set('user', encryptData(user.value), {
        sameSite: 'Lax',
      });

      setTimeout(() => {
        Cookies.remove('user');
        user.value = null;
      }, 86400000);

      isOn.value = user.value?.role === 'lessor';

      showNotification('Login Successful', 'Welcome back!', 'success');
      loginError.value = '';
    } else {
          logger.warn('Login response not successful', { status: response.status });
    }
  } catch (error) {
    handleLoginError(error);
  } finally {
    isLoading.value = false;
  }
};

  const handleCreateError = (error) => {
    if (error.response) {
      // Server responded with an error
      const errorMessage =
        error.response.data.error ||
        error.response.data.detail ||
        'Unknown error';
      showNotification('Info', errorMessage, 'info');
    } else if (error.request) {
      // Request was made but no response was received
      showNotification(
        'Network Error',
        'No response from server. Please check your connection.',
        'error'
      );
    } else {
      // Something else happened in setting up the request
      showNotification('Error', `Error: ${error.message}`, 'error');
    }
  };

  const handleLoginError = (error) => {
    let errorMessage = 'An error occurred. Please try again later.';
    let notificationType = 'error'; // Default notification type

    if (error.response) {
      // Extract error message from different possible fields
      errorMessage =
        error.response.data?.details ||
        error.response.data?.error ||
        error.response.data?.message ||
        errorMessage;

      switch (error.response.status) {
        case 400:
          errorMessage =
            errorMessage || 'Invalid request. Please check your input.';
          break;
        case 401:
          errorMessage =
            errorMessage || 'Incorrect email or password.';
          break;
        case 403:
          errorMessage =
            errorMessage ||
            'Account not verified. Please check your email.';
          notificationType = 'info'; // Change notification type to info
          break;
        case 429:
          errorMessage =
            errorMessage ||
            'Too many login attempts. Try again later.';
          break;
        case 500:
          errorMessage =
            errorMessage || 'Server error. Please try again later.';
          break;
        case 503:
          errorMessage =
            errorMessage ||
            'Service temporarily unavailable. Try again later.';
          break;
      }
    } else if (error.request) {
      // No response received (e.g., network issues)
      errorMessage = 'Network issue. Please check your connection.';
    } else {
      // Something else happened
      errorMessage = error.message || errorMessage;
    }

    // Update UI
    loginError.value = errorMessage;
    showNotification(
      `Login ${notificationType}`,
      errorMessage,
      notificationType,
      0
    );
  };

  const refreshToken = async () => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/accounts/token/refresh/`,
        {}, // No payload needed, as cookies are used
        { withCredentials: true } // Ensure cookies are sent with the request
      );

      if (response.status === 200) {
        return true; // Indicate success
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  axios.interceptors.response.use(
    (response) => response, // Pass through successful responses
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true; // Prevent infinite retry loop

        // Attempt to refresh the token
        const tokenRefreshed = await refreshToken();

        if (tokenRefreshed) {
          // Retry the original request with the refreshed token
          return axios(originalRequest);
        } else {
          // Redirect to login if refresh fails
          logger.warn(
            'Redirecting to login due to token refresh failure.'
          );
          // Clear user data
          user.value = null; // Reactive state for the user
          Cookies.remove('user'); // Remove user cookie
          router.push('/login'); // Ensure router is properly imported and accessible
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );

  const logout = async () => {
    isLoading.value = true;
    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/accounts/logout/`,
        {},
        { withCredentials: true }
      );
      // Clear user data
      user.value = null; // Reactive state for the user
      Cookies.remove('user'); // Remove user cookie

      if (response.status === 200) {
        user.value = null;
        Cookies.remove('user');
        showNotification(
          'Logout Successful',
          'You have been logged out.',
          'success',
          0
        );
        router.push('/');

        // Clear cart data
        cartStore.clearCart();
      }
    } catch (error) {
      // You can optionally show an error here
    } finally {
      isLoading.value = false; // Stop loading spinner
    }
  };

  return {
    isOn,
    user,
    redirectTo,
    loginError,
    isLoading,
    getCSRFToken,
    activeSection,
    showSidebar,
    login,
    logout,
    getUserData,
    encryptData,
    decryptData,
    refreshToken,
    isAuthenticated,
    updateUserRole,
    navigateToRoleSection,
  };
});
